import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../ui/badge';
import { CheckCircle2 } from 'lucide-react';

const SEVERITY = {
  critical: 'border-l-red-500',
  urgent: 'border-l-amber-500',
  normal: 'border-l-[hsl(var(--brand-accent))]',
};

const waitLabel = (item) => {
  const d = item.days_waiting;
  // An evaluation coming due is not something that has been waiting — say when
  // it is due, not how long it has sat.
  if (item.kind === 'eval_due') {
    const due = item.due_in_days;
    if (due === null || due === undefined) return '';
    if (due < 0) return `${Math.abs(due)} day${due === -1 ? '' : 's'} overdue`;
    if (due === 0) return 'Due today';
    return `Due in ${due} day${due === 1 ? '' : 's'}`;
  }
  if (item.kind === 'license') {
    if (!d) return 'Expiring soon';
    return `${d} day${d === 1 ? '' : 's'} overdue`;
  }
  if (d === null || d === undefined) return '';
  if (d === 0) return 'Today';
  return `Waiting ${d} day${d === 1 ? '' : 's'}`;
};

export const MyWorkWidget = ({ data }) => {
  const navigate = useNavigate();
  const items = data?.items || [];

  if (!items.length) {
    return (
      <div className="flex items-center gap-3 py-3" data-testid="my-work-empty">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
        <div>
          <p className="text-sm font-medium text-fg">Nothing is waiting on you.</p>
          <p className="text-xs text-fg-muted mt-0.5">
            Signatures, approvals and acknowledgements are all clear.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="my-work-list">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className="font-semibold" data-testid="my-work-total">
          {data.total} item{data.total === 1 ? '' : 's'}
        </Badge>
        {Object.entries(data.by_kind || {}).map(([kind, n]) => (
          <span key={kind} className="text-xs text-fg-muted">
            {n} {kind.replace(/_/g, ' ')}
          </span>
        ))}
      </div>

      <ul
        className="space-y-1.5 max-h-[26rem] overflow-y-auto pr-1"
        data-testid="my-work-scroll"
      >
        {items.map((item, i) => (
          <li key={`${item.kind}-${i}`}>
            <button
              type="button"
              onClick={() => navigate(item.link)}
              className={`w-full text-left flex items-start gap-3 p-2.5 rounded-md border-l-2
                ${SEVERITY[item.severity] || SEVERITY.normal}
                bg-surface-sunken hover:bg-[hsl(var(--hover-overlay))]
                transition-colors duration-150 focus-visible:outline-none
                focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand-accent))]`}
              data-testid={`my-work-item-${i}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-fg truncate">{item.title}</p>
                <p className="text-xs text-fg-muted truncate">
                  {item.label}{item.subtitle ? ` · ${item.subtitle}` : ''}
                </p>
              </div>
              <span className="text-xs text-fg-muted whitespace-nowrap shrink-0 pt-0.5">
                {waitLabel(item)}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {data.total > items.length && (
        <p className="text-xs text-fg-muted">
          Showing the {items.length} longest-waiting of {data.total}.
        </p>
      )}
      {data.partial && (
        <p className="text-xs text-amber-700">
          Part of this list could not be loaded.
        </p>
      )}
    </div>
  );
};

export default MyWorkWidget;
